import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import useAuthStore from '../store/authStore';
import dashboardConfigurationService, { 
  DashboardConfiguration, 
  DashboardOptions,
  CreateDashboardRequest,
  DashboardWidget,
  CreateWidgetRequest
} from '../services/dashboardConfigurationService';
import DashboardPreview from '../components/dashboard/DashboardPreview';
import ConfigurationSidebar from '../components/dashboard/ConfigurationSidebar';
import WidgetConfigurationModal from '../components/dashboard/WidgetConfigurationModal';
import './DashboardConfigurationPage.css';

interface DashboardConfigurationPageState {
  dashboard: DashboardConfiguration | null;
  availableDashboards: DashboardConfiguration[] | null;
  options: DashboardOptions | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  selectedWidget: DashboardWidget | null;
  showWidgetModal: boolean;
  activeTabIndex: number;
  draggedWidget: CreateWidgetRequest | null;
}

const DashboardConfigurationPage: React.FC = () => {
  const navigate = useNavigate();
  const { dashboardId } = useParams<{ dashboardId?: string }>();
  const { theme } = useTheme();
  const { user } = useAuthStore();

  const [state, setState] = useState<DashboardConfigurationPageState>({
    dashboard: null,
    availableDashboards: null,
    options: null,
    loading: true,
    saving: false,
    error: null,
    selectedWidget: null,
    showWidgetModal: false,
    activeTabIndex: 0,
    draggedWidget: null
  });

  const isEditMode = Boolean(dashboardId);

  // Load dashboard and options
  useEffect(() => {
    loadDashboardData();
  }, [dashboardId]);

  const loadDashboardData = async () => {
    console.log('🚀 loadDashboardData called');
    try {
      console.log('🔄 Setting loading state...');
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Load options and available dashboards
      console.log('🔄 About to call getDashboardOptions...');
      console.log('📊 Service instance:', dashboardConfigurationService);
      const options = await dashboardConfigurationService.getDashboardOptions();
      console.log('📊 Dashboard options loaded:', options);
      
      // Load available dashboards for the selector
      const availableDashboards = await dashboardConfigurationService.getDashboards();
      console.log('📊 Available dashboards loaded:', availableDashboards.length);

      let dashboard: DashboardConfiguration | null = null;

      if (isEditMode && dashboardId) {
        // Load existing dashboard
        dashboard = await dashboardConfigurationService.getDashboard(parseInt(dashboardId));
      } else {
        // Create new dashboard structure
        dashboard = {
          userId: parseInt(user?.id?.toString() || '0'),
          organizationId: parseInt(user?.organization_id?.toString() || '0'),
          name: 'New Dashboard',
          description: '',
          isDefault: false,
          isShared: false,
          tabs: [{
            tabName: 'Overview',
            tabIcon: 'dashboard',
            tabOrder: 0,
            isActive: true,
            widgets: []
          }]
        };
      }

      setState(prev => ({
        ...prev,
        dashboard,
        availableDashboards,
        options,
        loading: false
      }));

    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to load dashboard data',
        loading: false
      }));
    }
  };

  // Save dashboard
  const saveDashboard = async () => {
    console.log('🚀 saveDashboard called');
    console.log('📊 Current state.dashboard:', state.dashboard);
    
    if (!state.dashboard) {
      console.log('❌ No dashboard in state, returning early');
      return;
    }

    console.log('✅ Dashboard exists, proceeding with save...');
    
    try {
      setState(prev => ({ ...prev, saving: true, error: null }));

      let savedDashboard: DashboardConfiguration;

      if (isEditMode && state.dashboard.id) {
        // Update existing dashboard
        savedDashboard = await dashboardConfigurationService.updateDashboard(
          state.dashboard.id,
          state.dashboard
        );
      } else {
        // Create new dashboard
        const createRequest: CreateDashboardRequest = {
          name: state.dashboard.name,
          description: state.dashboard.description,
          isDefault: state.dashboard.isDefault,
          tabs: state.dashboard.tabs?.map(tab => ({
            tabName: tab.tabName,
            tabIcon: tab.tabIcon,
            tabDescription: tab.tabDescription,
            tabOrder: tab.tabOrder,
            widgets: tab.widgets?.map(widget => ({
              widgetName: widget.widgetName,
              widgetType: widget.widgetType,
              chartType: widget.chartType,
              positionX: widget.positionX,
              positionY: widget.positionY,
              width: widget.width,
              height: widget.height,
              widgetConfig: widget.widgetConfig,
              dataConfig: widget.dataConfig
            })) || []
          })) || []
        };

        console.log('📤 Creating dashboard with request:', createRequest);
        savedDashboard = await dashboardConfigurationService.createDashboard(createRequest);
        console.log('✅ Dashboard created successfully:', savedDashboard);
      }

      setState(prev => ({
        ...prev,
        dashboard: savedDashboard,
        saving: false
      }));

      // Navigate to edit mode if we were creating a new dashboard
      if (!isEditMode && savedDashboard.id) {
        navigate(`/dashboard-config/${savedDashboard.id}`, { replace: true });
      }

    } catch (error: any) {
      console.error('❌ Error saving dashboard:', error);
      console.error('❌ Full error details:', {
        message: error.message,
        status: error.status,
        response: error.response,
        stack: error.stack
      });
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to save dashboard',
        saving: false
      }));
    }
  };

  // Load specific dashboard
  const loadDashboard = useCallback(async (dashboardId: number) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const dashboard = await dashboardConfigurationService.getDashboard(dashboardId);
      
      setState(prev => ({
        ...prev,
        dashboard,
        loading: false
      }));
      
      // Navigate to edit mode
      navigate(`/dashboard-config/${dashboardId}`, { replace: true });
      
    } catch (error: any) {
      console.error('Error loading dashboard:', error);
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to load dashboard',
        loading: false
      }));
    }
  }, [navigate]);

  // Delete specific dashboard
  const deleteDashboard = useCallback(async (dashboardId: number) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      await dashboardConfigurationService.deleteDashboard(dashboardId);
      
      // Reload available dashboards
      const availableDashboards = await dashboardConfigurationService.getDashboards();
      
      // If we deleted the current dashboard, navigate to create new
      const shouldNavigateToNew = state.dashboard?.id === dashboardId;
      
      setState(prev => ({
        ...prev,
        availableDashboards,
        dashboard: shouldNavigateToNew ? null : prev.dashboard,
        loading: false
      }));
      
      if (shouldNavigateToNew) {
        navigate('/dashboard-config', { replace: true });
      }
      
    } catch (error: any) {
      console.error('Error deleting dashboard:', error);
      setState(prev => ({
        ...prev,
        error: error.message || 'Failed to delete dashboard',
        loading: false
      }));
    }
  }, [navigate, state.dashboard?.id]);

  // Update dashboard property
  const updateDashboard = useCallback((updates: Partial<DashboardConfiguration>) => {
    setState(prev => ({
      ...prev,
      dashboard: prev.dashboard ? { ...prev.dashboard, ...updates } : null
    }));
  }, []);

  // Add new tab
  const addTab = useCallback(async () => {
    if (!state.dashboard?.tabs) return;

    const newTab = {
      tabName: `Tab ${state.dashboard.tabs.length + 1}`,
      tabIcon: 'dashboard',
      tabOrder: state.dashboard.tabs.length,
      isActive: true,
      widgets: []
    };

    const updatedTabs = [...state.dashboard.tabs, newTab];
    const updatedDashboard = { ...state.dashboard, tabs: updatedTabs };
    
    updateDashboard({ tabs: updatedTabs });

    // Auto-save the dashboard with the new tab
    if (state.dashboard.id) {
      try {
        await dashboardConfigurationService.updateDashboard(
          state.dashboard.id,
          updatedDashboard
        );
        console.log('✅ Tab added and saved automatically');
      } catch (error) {
        console.error('❌ Error auto-saving new tab:', error);
      }
    }
  }, [state.dashboard, updateDashboard]);

  // Update tab
  const updateTab = useCallback(async (tabIndex: number, updates: any) => {
    if (!state.dashboard?.tabs) return;

    const updatedTabs = [...state.dashboard.tabs];
    updatedTabs[tabIndex] = { ...updatedTabs[tabIndex], ...updates };
    const updatedDashboard = { ...state.dashboard, tabs: updatedTabs };

    updateDashboard({ tabs: updatedTabs });

    // Auto-save the dashboard with the updated tab
    if (state.dashboard.id) {
      try {
        await dashboardConfigurationService.updateDashboard(
          state.dashboard.id,
          updatedDashboard
        );
        console.log('✅ Tab updated and saved automatically');
      } catch (error) {
        console.error('❌ Error auto-saving tab update:', error);
      }
    }
  }, [state.dashboard, updateDashboard]);

  // Delete tab
  const deleteTab = useCallback(async (tabIndex: number) => {
    if (!state.dashboard?.tabs || state.dashboard.tabs.length <= 1) return;

    const updatedTabs = state.dashboard.tabs.filter((_, index) => index !== tabIndex);
    const newActiveIndex = Math.min(state.activeTabIndex, updatedTabs.length - 1);
    const updatedDashboard = { ...state.dashboard, tabs: updatedTabs };

    updateDashboard({ tabs: updatedTabs });
    setState(prev => ({ ...prev, activeTabIndex: newActiveIndex }));

    // Auto-save the dashboard with the deleted tab
    if (state.dashboard.id) {
      try {
        await dashboardConfigurationService.updateDashboard(
          state.dashboard.id,
          updatedDashboard
        );
        console.log('✅ Tab deleted and saved automatically');
      } catch (error) {
        console.error('❌ Error auto-saving tab deletion:', error);
      }
    }
  }, [state.dashboard, state.activeTabIndex, updateDashboard]);

  // Add widget to current tab
  const addWidget = useCallback((widget: CreateWidgetRequest) => {
    if (!state.dashboard?.tabs) return;

    const updatedTabs = [...state.dashboard.tabs];
    const currentTab = updatedTabs[state.activeTabIndex];
    
    if (currentTab) {
      const newWidget: DashboardWidget = {
        ...widget,
        id: Date.now(), // Temporary ID for new widgets
        isActive: true,
        chartType: widget.chartType as any // Cast to satisfy type requirements
      };

      currentTab.widgets = [...(currentTab.widgets || []), newWidget];
      updateDashboard({ tabs: updatedTabs });
    }
  }, [state.dashboard, state.activeTabIndex, updateDashboard]);

  // Update widget
  const updateWidget = useCallback((widgetIndex: number, updates: Partial<DashboardWidget>) => {
    if (!state.dashboard?.tabs) return;

    const updatedTabs = [...state.dashboard.tabs];
    const currentTab = updatedTabs[state.activeTabIndex];
    
    if (currentTab?.widgets) {
      currentTab.widgets[widgetIndex] = { ...currentTab.widgets[widgetIndex], ...updates };
      updateDashboard({ tabs: updatedTabs });
    }
  }, [state.dashboard, state.activeTabIndex, updateDashboard]);

  // Delete widget
  const deleteWidget = useCallback((widgetIndex: number) => {
    if (!state.dashboard?.tabs) return;

    const updatedTabs = [...state.dashboard.tabs];
    const currentTab = updatedTabs[state.activeTabIndex];
    
    if (currentTab?.widgets) {
      currentTab.widgets = currentTab.widgets.filter((_, index) => index !== widgetIndex);
      updateDashboard({ tabs: updatedTabs });
    }
  }, [state.dashboard, state.activeTabIndex, updateDashboard]);

  // Open widget configuration modal
  const openWidgetModal = useCallback((widget?: DashboardWidget) => {
    setState(prev => ({
      ...prev,
      selectedWidget: widget || null,
      showWidgetModal: true
    }));
  }, []);

  // Close widget configuration modal
  const closeWidgetModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedWidget: null,
      showWidgetModal: false
    }));
  }, []);

  // Handle widget configuration save
  const handleWidgetSave = useCallback((widgetData: CreateWidgetRequest) => {
    if (state.selectedWidget) {
      // Update existing widget
      const widgetIndex = state.dashboard?.tabs?.[state.activeTabIndex]?.widgets?.findIndex(
        w => w.id === state.selectedWidget?.id
      ) ?? -1;
      
      if (widgetIndex >= 0) {
        updateWidget(widgetIndex, { ...widgetData, chartType: widgetData.chartType as any });
      }
    } else {
      // Add new widget
      addWidget(widgetData);
    }
    
    closeWidgetModal();
  }, [state.selectedWidget, state.dashboard, state.activeTabIndex, addWidget, updateWidget, closeWidgetModal]);

  if (state.loading) {
    return (
      <div className="dashboard-config-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard configuration...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="dashboard-config-page">
        <div className="error-container">
          <h2>❌ Error</h2>
          <p>{state.error}</p>
          <button onClick={loadDashboardData} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!state.dashboard || !state.options) {
    return (
      <div className="dashboard-config-page">
        <div className="error-container">
          <h2>⚠️ No Data</h2>
          <p>Dashboard configuration could not be loaded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-config-page ${theme}`}>
      {/* Header */}
      <div className="dashboard-config-header">
        <div className="header-left">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="back-button"
          >
            ← Back to Dashboard
          </button>
          <div className="header-info">
            <h1>{isEditMode ? 'Edit Dashboard' : 'Create Dashboard'}</h1>
            <p>{state.dashboard.name}</p>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            onClick={() => {
              console.log('🖱️ Save button clicked!');
              saveDashboard();
            }} 
            disabled={state.saving}
            className="save-button primary"
          >
            {state.saving ? '💾 Saving...' : '💾 Save Dashboard'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-config-content">
        {/* Sidebar */}
        <ConfigurationSidebar
          dashboard={state.dashboard}
          availableDashboards={state.availableDashboards || undefined}
          options={state.options}
          onDashboardUpdate={updateDashboard}
          onDashboardLoad={loadDashboard}
          onDashboardDelete={deleteDashboard}
          onAddTab={addTab}
          onUpdateTab={updateTab}
          onDeleteTab={deleteTab}
          onAddWidget={openWidgetModal}
          activeTabIndex={state.activeTabIndex}
          onTabChange={(index) => setState(prev => ({ ...prev, activeTabIndex: index }))}
        />

        {/* Preview Area */}
        <div className="dashboard-preview-area">
          <DashboardPreview
            dashboard={state.dashboard}
            activeTabIndex={state.activeTabIndex}
            onWidgetEdit={openWidgetModal}
            onWidgetDelete={deleteWidget}
            onWidgetMove={updateWidget}
          />
        </div>
      </div>

      {/* Widget Configuration Modal */}
      {state.showWidgetModal && state.options && (
        <WidgetConfigurationModal
          isOpen={state.showWidgetModal}
          widget={state.selectedWidget}
          availableKPIs={state.options.availableKpis || []}
          availableCalculations={(state.options.availableCalculations || []).map(calc => ({ 
            id: calc.id.toString(), 
            name: calc.name, 
            description: calc.description || '' 
          }))}
          colorPresets={(state.options.colorPresets || []).map(preset => ({ 
            id: preset.id.toString(), 
            name: preset.name, 
            colors: preset.colors 
          }))}
          onSave={handleWidgetSave}
          onClose={closeWidgetModal}
        />
      )}
    </div>
  );
};

export default DashboardConfigurationPage;
